import { Icon16PXClipBoard } from 'assets/icon-16-px-clip-board';
import { Icon16PXCreditCard } from 'assets/icon-16-px-credit-card';
import { Icon16PXPersonGroup } from 'assets/icon-16-px-person-group';
import { Icon16PXShield } from 'assets/icon-16-px-shield';
import { PhoneIcon } from 'assets/phone-icon';
import { PolicyholderDetailsIcon } from 'assets/policyholder-details-icon';
import { getPolicyPaymentMethod } from 'payment-methods/actions/get-policy-payment-method';
import { BANVStatus, banvStatusColorMapping, prettyBANVStatus } from 'payment-methods/domain/banv-status';
import {
  DebitOrderPaymentMethod,
  ExternalPaymentMethod,
  PaymentMethodAll,
} from 'payment-methods/domain/payment-method';
import { Policy } from 'policies/domain/policy';
import { PremiumDisplay } from 'policy-issuing/payment/payment-step';
import { ManagementView, PolicyManagementSceneBaseParams } from 'policy-management/policy-management-scene';
import { ProductModule } from 'product-modules/domain/product-module';
import React, { useState } from 'react';
import { LoadingInputs } from 'rootstrap/components-old/loaders/loading-lines';
import { FormWrapperStyle } from 'rootstrap/components-old/root-schema-form/root-schema-form';
import { Badge } from 'rootstrap/components/badge/styles';
import NewSpinner, { AnimationTypes, SpinnerSize } from 'rootstrap/components/spinner/new-spinner';
import { Spacing } from 'rootstrap/global-styles';
import { useEmbedParamsContext } from 'shared/embed-params-context';
import { usePromise } from 'shared/hooks/promise';
import { getPremiumDisplay } from 'shared/utils';
import { getColor, getWording } from 'site-config';
import { useSiteConfigContext } from 'style-context';
import { StyledPolicyDetailsScene } from './styles/policy-details-styles';
import {
  ButtonContentContainer,
  PolicyViewBodyWrapper,
  PolicyViewFooterStyle,
  PolicyViewLoadingWrapper,
  PolicyViewSplashContainer,
  PolicyViewStatusBadge,
  PolicyViewStatusBadgeWrapper,
  PolicyViewSuccessButton,
  PolicyViewSummaryPanel,
  TextAndBadgeContainer,
} from './styles/policy-view-styles';
import { policyStatusColorMapping } from './views/policy-status-display';
import { PolicySummaryErrorPage } from './views/policy-summary-error-page';

export interface PolicyDetailsProps extends PolicyManagementSceneBaseParams {
  productModule: ProductModule | undefined;
  policy: Policy | undefined;
  setView: (v: ManagementView) => void;
}

export const PolicyViewScene = (props: PolicyDetailsProps) => {
  const { policy, setView, isLoading: pageLoading, error: pageError, productModule } = props;
  const { siteConfig } = useSiteConfigContext();
  const { embedParams } = useEmbedParamsContext();
  const { auth, environment, organizationId } = embedParams;

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodAll | undefined>();

  const { isLoading: paymentMethodLoading } = usePromise(async () => {
    if (!policy || !policy.policyId) {
      return;
    }

    const pm = await getPolicyPaymentMethod({
      auth,
      environment,
      organizationId,
      policyId: policy.policyId,
    });
    setPaymentMethod(pm);
  }, [policy?.policyId, auth, environment, organizationId]);

  const primaryColor = getColor({ siteConfig, color: 'primary' });
  const contactNumberWording = getWording({ wording: siteConfig?.management?.policyView.wording.contactNumber });
  const { Beneficiaries, Claims, PolicyDetails, PaymentDetails, Policyholder } = ManagementView;
  const canEditBillingDay = siteConfig?.management?.payment.displayOptionalSections.editBillingDay;
  const canEditPaymentMethod = siteConfig?.management?.payment.displayOptionalSections.editPaymentMethod;
  const canViewPaymentMethod = siteConfig?.management?.payment.displayOptionalSections.viewPaymentMethod;
  const displayClaimSection = siteConfig?.management?.claim?.displayOptionalSections?.displayClaimSection ?? true;
  const displayBeneficiariesSection =
    siteConfig?.management?.beneficiaries?.displayOptionalSections?.displayBeneficiariesSection ?? true;

  const displayPaymentButton = canEditBillingDay || canEditPaymentMethod || canViewPaymentMethod;

  const premiumDisplay = getPremiumDisplay({
    premiumValue: policy?.basePremium,
    billingFrequency: policy?.billingFrequency,
    currency: productModule?.productModuleDefinition?.settings.billing.currency,
  });

  const isLoading = pageLoading || paymentMethodLoading;
  const error = pageError;

  if (error) {
    return <PolicySummaryErrorPage />;
  }

  return (
    <>
      <StyledPolicyDetailsScene isLoading={isLoading}>
        {policy && (
          <PolicyViewStatusBadgeWrapper>
            <PolicyViewStatusBadge color={policyStatusColorMapping(policy.status, siteConfig)}>
              {policy.prettyPolicyStatus()}
            </PolicyViewStatusBadge>
          </PolicyViewStatusBadgeWrapper>
        )}
        <PremiumDisplay siteConfig={siteConfig}>
          <p>Ongoing premium</p>
          {!isLoading && (
            <>
              <div>{premiumDisplay}</div>
              <span>Next payment: {policy?.nextBillingDate?.format('DD MMMM')}</span>
            </>
          )}
          {isLoading && <NewSpinner animation={AnimationTypes.Border} size={SpinnerSize.sm} color='FFFFFF' />}
        </PremiumDisplay>
        <PolicyViewSplashContainer siteConfig={siteConfig}>
          <PolicyViewSummaryPanel siteConfig={siteConfig}>
            <PolicyViewBodyWrapper>
              {isLoading && !error && (
                <PolicyViewLoadingWrapper>
                  <LoadingInputs count={1} />
                </PolicyViewLoadingWrapper>
              )}
              {!isLoading && !error && (
                <>
                  <PolicyViewSuccessButton
                    outline
                    siteConfig={siteConfig}
                    onClick={() => {
                      setView(PolicyDetails);
                    }}
                    fullWidth={true}
                  >
                    <ButtonContentContainer>
                      <Icon16PXShield fill={primaryColor} />
                      <span>View policy details</span>
                    </ButtonContentContainer>
                  </PolicyViewSuccessButton>
                  <PolicyViewSuccessButton
                    id='edit-personal-details-button'
                    outline
                    siteConfig={siteConfig}
                    onClick={() => {
                      setView(Policyholder);
                    }}
                    fullWidth={true}
                  >
                    <ButtonContentContainer>
                      <PolicyholderDetailsIcon fill={primaryColor} />
                      <span>Edit personal details</span>
                    </ButtonContentContainer>
                  </PolicyViewSuccessButton>
                  {displayClaimSection && (
                    <PolicyViewSuccessButton
                      id='make-a-claim'
                      outline
                      siteConfig={siteConfig}
                      onClick={() => {
                        setView(Claims);
                      }}
                      fullWidth={true}
                    >
                      <ButtonContentContainer>
                        <Icon16PXClipBoard fill={primaryColor} />
                        <span>Make a claim</span>
                      </ButtonContentContainer>
                    </PolicyViewSuccessButton>
                  )}
                  {displayBeneficiariesSection && (
                    <PolicyViewSuccessButton
                      id='your-beneficiaries'
                      outline
                      siteConfig={siteConfig}
                      onClick={() => {
                        setView(Beneficiaries);
                      }}
                      fullWidth={true}
                    >
                      <ButtonContentContainer>
                        <Icon16PXPersonGroup fill={primaryColor} />
                        <span>Your beneficiaries</span>
                      </ButtonContentContainer>
                    </PolicyViewSuccessButton>
                  )}
                  {displayPaymentButton && (
                    <PolicyViewSuccessButton
                      outline
                      siteConfig={siteConfig}
                      onClick={() => {
                        setView(PaymentDetails);
                      }}
                      fullWidth={true}
                    >
                      <ButtonContentContainer>
                        <Icon16PXCreditCard fill={primaryColor} />
                        <TextAndBadgeContainer>
                          <span>Your payment details</span>
                          {(() => {
                            let statusToShow: BANVStatus | undefined = undefined;
                            if (paymentMethod instanceof DebitOrderPaymentMethod) {
                              statusToShow = paymentMethod.bankDetails?.banvStatus;
                            } else if (paymentMethod instanceof ExternalPaymentMethod) {
                              statusToShow = paymentMethod.banvStatus;
                            }

                            if (statusToShow) {
                              const backgroundColor = banvStatusColorMapping(statusToShow, siteConfig);
                              const fontColor = '#FFFFFF';
                              return (
                                <Badge
                                  id='banv-status-badge'
                                  style={{ marginLeft: Spacing.md }}
                                  backgroundColor={backgroundColor}
                                  fontColor={fontColor}
                                >
                                  {prettyBANVStatus(statusToShow)}
                                </Badge>
                              );
                            }
                            return null;
                          })()}
                        </TextAndBadgeContainer>
                      </ButtonContentContainer>
                    </PolicyViewSuccessButton>
                  )}
                </>
              )}
            </PolicyViewBodyWrapper>
          </PolicyViewSummaryPanel>
        </PolicyViewSplashContainer>
      </StyledPolicyDetailsScene>
      <FormWrapperStyle>
        <PolicyViewFooterStyle siteConfig={siteConfig}>
          <div>{siteConfig?.management?.policyView.wording.contactDescription}</div>
          <PhoneIcon className='phone-icon' fill={getColor({ siteConfig, color: 'highlight' })} />
          Call us on <a href={`tel:${contactNumberWording}`}>{contactNumberWording}</a>
        </PolicyViewFooterStyle>
      </FormWrapperStyle>
    </>
  );
};
